import * as React from "react";
import { useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { get, map } from "lodash";
import {
  DropdownItemProps,
  Form,
  Grid,
  Header,
  Icon,
  Message,
  Popup,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "semantic-ui-react";

import { CoachComponents } from "@/interfaces";
import { BasicFormProps } from "@/interfaces";
import { CoachService } from "@/services";
import { SegmentationDropDown } from "./components/SegmenationDropDown";

import { AssistantSignUpType, descriptors } from "./descriptors";
import "./styles.scss";
import { useAccountSettings } from "@/utils";

interface OwnProps extends BasicFormProps {
  /**
   * Assistant creation date / null when on creation view
   */
  createdDate?: string;

  /**
   * Assistant latest login date
   */
  latestLoginDate?: string;

  /**
   * Forms ref - calls submit outside form
   */
  formSubmitRef: any;

  coachId: number;
  /*
   * Indicate if coach has write permissions
   */
  withWritePermission?: boolean;
  /**
   * Handle radio button changes - Permission's changes
   */
  handleSetFieldValue: (field: string, value: any) => void;
}

type Props = OwnProps;

/**
 * Represents a Sub coach SignUp screen.
 */
const AssistantFormFC: React.FC<Props> = ({
  values,
  messages,
  isSubmitting,
  formSubmitRef,
  createdDate,
  latestLoginDate,
  coachId,

  handleChange,
  handleSubmit,
  handleSetFieldValue,
  withWritePermission,
}) => {
  const { formatMessage } = useIntl();
  const errors = messages && messages.error && messages.error.length > 0;
  const warnings = messages && messages.warn && messages.warn.length > 0;

  const { hasAccessToCoachComponent } = useAccountSettings();
  const permissions = useMemo(() => {
    return Object.values(CoachComponents).filter(permission => {
      return hasAccessToCoachComponent(permission.id);
    });
  }, [hasAccessToCoachComponent]);

  const [selectedBranches, setSelectedBranches] = React.useState<string[] | undefined>(values.branches);
  const [selectedRegions, setSelectedRegions] = React.useState<string[] | undefined>(values.regions);

  const [branchesFilters, setBranchesFilters] = React.useState<DropdownItemProps[]>([]);
  const [regionFilters, setRegionsFilters] = React.useState<DropdownItemProps[]>([]);

  React.useEffect(() => {
    handleSetFieldValue("branches", selectedBranches);
  }, [selectedBranches]);

  React.useEffect(() => {
    handleSetFieldValue("regions", selectedRegions);
  }, [selectedRegions]);

  React.useEffect(() => {
    CoachService.branches(coachId).then(response => {
      const filters: DropdownItemProps[] = map(response, branch => ({
        key: branch.branchId,
        text: branch.name,
        value: branch.branchId,
      }));
      setBranchesFilters(filters);
    });
    CoachService.regions(coachId).then(response => {
      const filters: DropdownItemProps[] = map(response, region => ({
        key: region.regionId,
        text: region.name,
        value: region.regionId,
      }));
      setRegionsFilters(filters);
    });
  }, [coachId]);

  const permissionsHandleChange = (e: any, data: any) => {
    handleSetFieldValue("permissionsMap." + data.name, data.value);
  };

  const isExisting = useMemo(() => {
    return !!values.userId;
  }, [values.userId]);

  const changeAllPermissions = (level: string) => {
    permissions.forEach(p => {
      handleSetFieldValue("permissionsMap." + p.id, level);
    });
  };

  const fieldsSection = (
    <Grid.Column>
      <Header as="h4" className={"permissionTitle"}>
        Account Information
      </Header>

      <Form.Input
        className={"scFormInput"}
        key={"firstName"}
        data-elm-id={"assistantSignUpInputFirstName"}
        fluid
        id={"firstName"}
        type={"text"}
        label={"First Name"}
        name={"firstName"}
        placeholder={"First Name"}
        onChange={handleChange}
        value={values.firstName}
        disabled={isSubmitting}
      />

      <Form.Input
        className={"scFormInput"}
        key={"lastName"}
        data-elm-id={"assistantSignUpInputLastName"}
        fluid
        id={"lastName"}
        type={"text"}
        label={"Last Name"}
        name={"lastName"}
        placeholder={"Last Name"}
        onChange={handleChange}
        value={values.lastName}
        disabled={isSubmitting}
      />

      <Form.Input
        className={"scFormInput"}
        key={"email"}
        data-elm-id={"assistantSignUpInputEmail"}
        fluid
        label={"Email"}
        id={"email"}
        type={"email"}
        name={"email"}
        placeholder={"Email"}
        onChange={handleChange}
        value={values.email}
        disabled={isSubmitting || isExisting}
      />
      {!isExisting && (
        <Form.Input
          className={"scFormInput"}
          key={"password"}
          data-elm-id={"assistantSignUpInputPassword"}
          fluid
          label={"Password"}
          icon={
            <Popup trigger={<Icon name="info circle" className={"infoIcon"} link />}>
              <FormattedMessage {...descriptors[AssistantSignUpType.passwordWithNumber]} />
              <br />
              <FormattedMessage {...descriptors[AssistantSignUpType.passwordWithCapital]} />
              <br />
              <FormattedMessage {...descriptors[AssistantSignUpType.passwordWithSpecialCharacter]} />
              <br />
              <FormattedMessage {...descriptors[AssistantSignUpType.passwordMin]} />
            </Popup>
          }
          id={"password"}
          type={"password"}
          name={"password"}
          placeholder={"Password"}
          onChange={handleChange}
          value={values.password}
          disabled={isSubmitting}
        />
      )}

      <Header as="h4" className={"permissionTitle"}>
        Assignments
      </Header>

      {/* BRANCH */}
      <SegmentationDropDown
        placeHolder={formatMessage({
          ...descriptors[AssistantSignUpType.branches],
        })}
        type={formatMessage({
          ...descriptors[AssistantSignUpType.branches],
        })}
        elements={branchesFilters}
        selectedElements={selectedBranches}
        setFunction={setSelectedBranches}
      />
      {/* REGION */}
      <SegmentationDropDown
        placeHolder={formatMessage({
          ...descriptors[AssistantSignUpType.regions],
        })}
        type={formatMessage({ ...descriptors[AssistantSignUpType.regions] })}
        elements={regionFilters}
        selectedElements={selectedRegions}
        setFunction={setSelectedRegions}
      />

      {createdDate && (
        <>
          <Grid.Row>
            <label className={"permissionLabel"}>Date Added {createdDate}</label>
          </Grid.Row>
          <Grid.Row>
            <label className={"permissionLabel"}>Last Login {latestLoginDate}</label>
          </Grid.Row>
        </>
      )}
    </Grid.Column>
  );

  const permissionsSection = (
    <Grid.Column className={isSubmitting ? "disabledForm" : ""}>
      <Form.Group grouped key={"assistantSignUpGroup2Permissions"}>
        <Header as="h4" className={"permissionTitle"}>
          Access
        </Header>
        <div className={"permissionsTableContainer"}>
          <Table compact>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Component</TableHeaderCell>
                <TableHeaderCell>
                  <a className={"permissionHeader"} onClick={() => changeAllPermissions("NONE")}>
                    None
                  </a>
                </TableHeaderCell>
                <TableHeaderCell>
                  <a className={"permissionHeader"} onClick={() => changeAllPermissions("READ")}>
                    Read
                  </a>
                </TableHeaderCell>
                <TableHeaderCell>
                  <a className={"permissionHeader"} onClick={() => changeAllPermissions("WRITE")}>
                    Write
                  </a>
                </TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {permissions.map((permission, index) => {
                return (
                  <TableRow key={`permission-row-${index}`}>
                    <TableCell>{permission.label}</TableCell>
                    <TableCell>
                      <Form.Radio
                        value={"NONE"}
                        onChange={permissionsHandleChange}
                        name={permission.id}
                        checked={values.permissionsMap[permission.id] === "NONE"}
                      />
                    </TableCell>
                    <TableCell>
                      <Form.Radio
                        value={"READ"}
                        onChange={permissionsHandleChange}
                        name={permission.id}
                        checked={values.permissionsMap[permission.id] === "READ"}
                      />
                    </TableCell>
                    <TableCell>
                      <Form.Radio
                        value={"WRITE"}
                        onChange={permissionsHandleChange}
                        name={permission.id}
                        checked={values.permissionsMap[permission.id] === "WRITE"}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Form.Group>
    </Grid.Column>
  );

  const infoMessages = get(messages, "info", []);
  const warningMessages = get(messages, "warn", []);
  const errorMessages = get(messages, "error", []);

  return (
    <Grid
      columns={16}
      verticalAlign="middle"
      stretched
      container
      textAlign={"left"}
      className={
        isSubmitting || !withWritePermission ? "assistantForm floatedGrid disabledForm" : "assistantForm floatedGrid"
      }
    >
      <Grid.Column width={16}>
        <Form
          size="large"
          error={errors}
          warning={warnings}
          ref={formSubmitRef}
          onSubmit={handleSubmit}
          className={"floatedFormBox"}
          loading={isSubmitting}
          disabled={!withWritePermission}
        >
          {errorMessages.length > 0 && <Message error header={"Errors:"} list={errorMessages} />}
          {warningMessages.length > 0 && <Message warning header={"Warnings: "} list={warningMessages} />}
          {infoMessages.length > 0 && <Message header={"Info: "} list={infoMessages} />}

          <Grid columns={2} padded>
            {fieldsSection}
            {permissionsSection}
          </Grid>
        </Form>
      </Grid.Column>
    </Grid>
  );
};

export const AssistantForm = AssistantFormFC;
